'use strict';

import Swiper, { Navigation, A11y } from 'swiper';
import 'swiper/css';
import 'swiper/css/a11y';

import VideoPlayer from '../utils/video-player';

const SliderVideo = {
	els: null,

	init: function () {
		SliderVideo.els = document.querySelectorAll('.f-slider-video');

		if ( ! SliderVideo.els || SliderVideo.els.length === 0 ) {
			return;
		}

		SliderVideo.els.forEach( el => SliderVideo.initBlock( el ) );
	},

	initBlock: function ( block ) {
		const sliderEl = block.querySelector('.f-slider-video__slider');

		if ( ! sliderEl ) {
			return;
		}

		const counterEl       = block.querySelector('.f-slider-video__nav-current');
		const mobileCaptionEl = block.querySelector('.f-slider-video__mobile-caption');
		const totalSlides     = parseInt( sliderEl.dataset.total, 10 ) || 1;

		// ── Swiper ────────────────────────────────────────────────────────────
		let swiper = null;

		if ( totalSlides > 1 ) {
			swiper = new Swiper( sliderEl, {
				modules: [ Navigation, A11y ],
				a11y: true,
				slidesPerView: 1,
				spaceBetween: 32,
				loop: false,
				speed: 600,
				navigation: {
					prevEl: block.querySelector('.f-slider-video__nav-btn--prev'),
					nextEl: block.querySelector('.f-slider-video__nav-btn--next'),
					disabledClass: 'is-disabled',
				},
				on: {
					slideChange: function () {
						const leavingSlide = block.querySelector('.swiper-slide-active');
						if ( leavingSlide ) {
							SliderVideo.resetSlide( leavingSlide );
						}
					},
					slideChangeTransitionEnd: function () {
						if ( counterEl ) {
							counterEl.textContent = String( this.activeIndex + 1 ).padStart( 2, '0' );
						}

						SliderVideo.syncMobileCaption( block, this.activeIndex, mobileCaptionEl );
					},
				},
			} );
		}

		// ── Click card to open popup + cursor-follow ─────────────────────────
		const pointerFine = window.matchMedia( '(pointer: fine)' ).matches;

		block.querySelectorAll( '.f-slider-video__card[data-video-format]' ).forEach( card => {
			card.addEventListener( 'click', () => SliderVideo.openPopup( block, card ) );

			if ( pointerFine ) {
				SliderVideo.initCursorPlay( card );
			}
		} );

		// ── Popup controls ────────────────────────────────────────────────────
		SliderVideo.initPopupControls( block );

		// ── Caption close ─────────────────────────────────────────────────────
		block.querySelectorAll( '.f-slider-video__caption-close' ).forEach( btn => {
			btn.addEventListener( 'click', e => {
				e.stopPropagation();
				const caption = btn.closest( '.f-slider-video__caption' );
				if ( caption ) {
					caption.classList.add( 'is-hidden' );
				}
			} );
		} );
	},

	resetSlide: function ( slide ) {
		const poster  = slide.querySelector('.f-slider-video__player-poster');
		const caption = slide.querySelector('.f-slider-video__caption');

		if ( poster ) {
			poster.classList.remove('hidden');

			const card = poster.closest('.f-slider-video__card') || slide;
			VideoPlayer.reset( card );
		}

		if ( caption ) {
			caption.classList.remove('is-hidden');
		}
	},

	syncMobileCaption: function ( block, activeIndex, mobileCaptionEl ) {
		if ( ! mobileCaptionEl ) {
			return;
		}

		const slides = block.querySelectorAll('.f-slider-video__slide');
		const active = slides[ activeIndex ];

		if ( ! active ) {
			return;
		}

		const caption = active.querySelector('.f-slider-video__caption');

		if ( ! caption ) {
			mobileCaptionEl.innerHTML = '';
			return;
		}

		const quote = caption.querySelector('.f-slider-video__caption-quote');
		const name  = caption.querySelector('.f-slider-video__caption-name');
		const role  = caption.querySelector('.f-slider-video__caption-role');

		let html = '';

		if ( quote ) {
			html += `<blockquote class="f-slider-video__mobile-caption-quote">${ quote.textContent }</blockquote>`;
		}

		if ( name || role ) {
			html += '<p class="f-slider-video__mobile-caption-meta">';
			if ( name ) {
				html += `<strong class="f-slider-video__mobile-caption-name">${ name.textContent }</strong>`;
			}
			if ( name && role ) {
				html += '<span class="f-slider-video__mobile-caption-sep" aria-hidden="true">|</span>';
			}
			if ( role ) {
				html += `<span class="f-slider-video__mobile-caption-role">${ role.textContent }</span>`;
			}
			html += '</p>';
		}

		mobileCaptionEl.innerHTML = html;
	},

	// ── Cursor-following play button ─────────────────────────────────────────
	initCursorPlay: function ( card ) {
		const playBtn = card.querySelector( '.f-slider-video__player-poster-play' );

		if ( ! playBtn ) {
			return;
		}

		card.addEventListener( 'mousemove', e => {
			const rect = card.getBoundingClientRect();
			const x    = e.clientX - rect.left;
			const y    = e.clientY - rect.top;

			playBtn.style.left = x + 'px';
			playBtn.style.top  = y + 'px';
		} );

		card.addEventListener( 'mouseleave', () => {
			playBtn.style.left = '';
			playBtn.style.top  = '';
		} );
	},

	// ── Video popup ──────────────────────────────────────────────────────────
	openPopup: function ( block, card ) {
		const popup    = block.querySelector( '.f-slider-video__popup' );
		const playerEl = popup ? popup.querySelector( '.f-slider-video__popup-player' ) : null;

		if ( ! popup || ! playerEl ) {
			return;
		}

		const format  = card.dataset.videoFormat || '';
		const videoId = card.dataset.videoId    || '';
		const videoUrl  = card.dataset.videoUrl  || '';
		const videoMime = card.dataset.videoMime || 'video/mp4';
		const posterUrl = card.dataset.videoPoster || '';

		// Clear previous
		playerEl.innerHTML = '';

		// Build player element safely via DOM API
		switch ( format ) {
			case 'youtube': {
				const iframe = document.createElement( 'iframe' );
				// videoId is esc_attr'd server-side; only allow word chars and hyphens
				const safeId = videoId.replace( /[^\w-]/g, '' );
				iframe.src = `https://www.youtube.com/embed/${ safeId }?autoplay=1&playsinline=1`;
				iframe.setAttribute( 'frameborder', '0' );
				iframe.setAttribute( 'allow', 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture' );
				iframe.setAttribute( 'allowfullscreen', '' );
				iframe.setAttribute( 'title', 'Video player' );
				playerEl.appendChild( iframe );
				break;
			}

			case 'vimeo': {
				const iframe    = document.createElement( 'iframe' );
				const separator = videoId.includes( '?' ) ? '&' : '?';
				iframe.src = `https://player.vimeo.com/video/${ videoId }${ separator }badge=0&autoplay=1&autopause=0&player_id=0&app_id=58479`;
				iframe.setAttribute( 'frameborder', '0' );
				iframe.setAttribute( 'allow', 'autoplay; fullscreen; picture-in-picture; clipboard-write' );
				iframe.setAttribute( 'allowfullscreen', '' );
				iframe.setAttribute( 'title', 'Video player' );
				playerEl.appendChild( iframe );
				break;
			}

			case 'internal':
			case 'mp4': {
				const video = document.createElement( 'video' );
				video.controls   = true;
				video.autoplay   = true;
				video.playsInline = true;
				if ( posterUrl ) {
					video.poster = posterUrl;
				}
				const source = document.createElement( 'source' );
				source.src  = videoUrl;
				source.type = videoMime;
				video.appendChild( source );
				playerEl.appendChild( video );
				break;
			}

			default:
				return;
		}

		popup.removeAttribute( 'hidden' );
		popup.setAttribute( 'aria-hidden', 'false' );
		document.body.classList.add( 'has-video-popup' );

		// rAF ensures display is applied before class triggers transition
		requestAnimationFrame( () => {
			requestAnimationFrame( () => {
				popup.classList.add( 'is-open' );
			} );
		} );

		const closeBtn = popup.querySelector( '.f-slider-video__popup-close' );
		if ( closeBtn ) {
			closeBtn.focus();
		}
	},

	closePopup: function ( block ) {
		const popup    = block.querySelector( '.f-slider-video__popup' );
		const playerEl = popup ? popup.querySelector( '.f-slider-video__popup-player' ) : null;

		if ( ! popup ) {
			return;
		}

		popup.classList.remove( 'is-open' );
		popup.setAttribute( 'aria-hidden', 'true' );
		document.body.classList.remove( 'has-video-popup' );

		// Wait for fade-out transition before hiding and clearing media
		const onEnd = () => {
			popup.setAttribute( 'hidden', '' );
			if ( playerEl ) {
				playerEl.innerHTML = '';
			}
			popup.removeEventListener( 'transitionend', onEnd );
		};

		popup.addEventListener( 'transitionend', onEnd );
	},

	initPopupControls: function ( block ) {
		const popup = block.querySelector( '.f-slider-video__popup' );

		if ( ! popup ) {
			return;
		}

		const overlay  = popup.querySelector( '.f-slider-video__popup-overlay' );
		const closeBtn = popup.querySelector( '.f-slider-video__popup-close' );

		if ( overlay ) {
			overlay.addEventListener( 'click', () => SliderVideo.closePopup( block ) );
		}

		if ( closeBtn ) {
			closeBtn.addEventListener( 'click', () => SliderVideo.closePopup( block ) );
		}

		document.addEventListener( 'keydown', e => {
			if ( e.key === 'Escape' && ! popup.hasAttribute( 'hidden' ) ) {
				SliderVideo.closePopup( block );
			}
		} );
	},
};

export default SliderVideo;
